import type { InFlightLine, InFlightOrder } from '../types'

// R462: the aggregator's own predicate (order-ready-aggregator.service.ts): a 'meal' line is
// the kitchen's, anything else is the shelf's.
export function isKitchenLine(line: InFlightLine): boolean {
  return line.productType === 'meal'
}

export function groupLines(order: InFlightOrder): { shelf: InFlightLine[]; kitchen: InFlightLine[] } {
  const lines = Array.isArray(order.lines) ? order.lines : []
  return { shelf: lines.filter((l) => !isKitchenLine(l)), kitchen: lines.filter(isKitchenLine) }
}

// Every kitchen line is ready. An order with no kitchen line is never "kitchen done".
export function kitchenDone(order: InFlightOrder): boolean {
  const { kitchen } = groupLines(order)
  return kitchen.length > 0 && kitchen.every((l) => l.kitchenTaskStatus === 'COMPLETED')
}

// Packed is offered only where the server will take it: a mixed order parked after the last Ready.
export function needsPack(order: InFlightOrder): boolean {
  return order.status === 'AWAITING_RETAIL_PACK'
}

// A shelf-only order goes straight to final check and never needs the kitchen or Packed, so
// the till does not list it here; kitchen-only and mixed orders are listed while cooking.
export function shownInFlight(order: InFlightOrder): boolean {
  return groupLines(order).kitchen.length > 0
}

/**
 * The ids whose kitchen turned done between two reads: seen with a kitchen line not ready,
 * now with every kitchen line ready. `seenCooking` is updated in place; `alreadyRung` keeps
 * an order from ringing twice.
 */
export function newlyKitchenDone(
  orders: InFlightOrder[],
  seenCooking: Set<string>,
  alreadyRung: Set<string>,
): string[] {
  const done: string[] = []
  for (const order of orders) {
    if (!shownInFlight(order)) continue
    if (!kitchenDone(order)) {
      seenCooking.add(order.id)
      continue
    }
    if (seenCooking.has(order.id) && !alreadyRung.has(order.id)) {
      alreadyRung.add(order.id)
      done.push(order.id)
    }
  }
  return done
}

/**
 * R462 ruling 1 (Dee: "when the kitchen marks its items ready it beeps on Nour's side"): a
 * kitchen-only order leaves the in-flight list on its last Ready and lands in the Final check
 * list. The ids seen cooking on this page that now sit in the Final check list ring, once per
 * order. A cancelled order is in neither list and never rings; nothing seen before this page
 * loaded rings.
 */
export function arrivedAtFinalCheck(
  finalCheckIds: string[],
  seenCooking: Set<string>,
  alreadyRung: Set<string>,
): string[] {
  const arrived: string[] = []
  for (const id of finalCheckIds) {
    if (seenCooking.has(id) && !alreadyRung.has(id)) {
      alreadyRung.add(id)
      arrived.push(id)
    }
  }
  return arrived
}
